import { Button, Input, Select } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';

import Modal from '../../foundation/components/modal/Modal';
import { useGetReportVariantCategoryOptionsQuery } from '../api/globalDataApi';
import { useUpdateVariantReportCategoryMutation } from '../api/variantApi';
import type {
  LinkedVariant,
  VariantReportData,
} from '../probands_samples/types';
import type { SampleDetail } from './types';

type SampleDetailReportCategoryModalProps = {
  open: boolean;
  reportData?: VariantReportData;
  sample?: SampleDetail;
  variant?: LinkedVariant;
  onClose: () => void;
};

const SampleDetailReportCategoryModal = ({
  open,
  reportData,
  sample,
  variant,
  onClose,
}: SampleDetailReportCategoryModalProps) => {
  const { data: reportCategoryOptions = [] } =
    useGetReportVariantCategoryOptionsQuery();
  const [updateReportCategory, { isLoading }] =
    useUpdateVariantReportCategoryMutation();
  const [selectedReportDataId, setSelectedReportDataId] = useState<
    number | undefined
  >();
  const [reportCategory, setReportCategory] = useState<string | undefined>();

  const selectedConditions = useMemo(
    () => variant?.report_data ?? [],
    [variant?.report_data],
  );

  const selectedReportData = useMemo(
    () =>
      selectedConditions.find(
        (condition) => condition.id === selectedReportDataId,
      ),
    [selectedConditions, selectedReportDataId],
  );

  const selectedConditionOptions = useMemo(
    () =>
      selectedConditions.map((condition) => ({
        label: condition.medicalinvestigation_name,
        value: condition.id,
      })),
    [selectedConditions],
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    setSelectedReportDataId(reportData?.id);
    setReportCategory(reportData?.report_category);
  }, [open, reportData?.id, reportData?.report_category]);

  const handleSelectedConditionChange = (nextReportDataId: number) => {
    const nextReportData = selectedConditions.find(
      (condition) => condition.id === nextReportDataId,
    );

    setSelectedReportDataId(nextReportDataId);
    setReportCategory(nextReportData?.report_category);
  };

  const handleSave = async () => {
    if (!selectedReportData?.id || !reportCategory) {
      return;
    }

    await updateReportCategory({
      reportvariantcategory_id: selectedReportData.id,
      requestData: {
        report_category: reportCategory,
      },
      sample_id: sample?.id,
      variant_id: variant?.variant_id ?? undefined,
    }).unwrap();
    onClose();
  };

  return (
    <Modal
      open={open}
      title="Please select a report category for the panel."
      onCancel={onClose}
      footer={null}
      width={760}
      className="sample-detail-report-category-modal"
    >
      <div className="sample-detail-report-category-modal__field">
        <div className="sample-detail-report-category-modal__label">
          Sample ID:
        </div>
        <Input value={sample?.sample_id ?? ''} disabled />
      </div>

      <div className="sample-detail-report-category-modal__field">
        <div className="sample-detail-report-category-modal__label">
          Report panel:
        </div>
        <Select
          className="sample-detail-report-category-modal__select"
          value={selectedReportDataId}
          onChange={handleSelectedConditionChange}
          options={selectedConditionOptions}
          placeholder="Choose report panel"
        />
      </div>

      <div className="sample-detail-report-category-modal__field">
        <div className="sample-detail-report-category-modal__label">
          Report category:
        </div>
        <Select
          className="sample-detail-report-category-modal__select"
          value={reportCategory}
          onChange={setReportCategory}
          options={reportCategoryOptions}
          placeholder="Choose report category"
        />
      </div>

      <div className="sample-detail-report-category-modal__actions">
        <Button
          type="primary"
          loading={isLoading}
          disabled={!reportCategory || !selectedReportData}
          onClick={handleSave}
        >
          Save changes
        </Button>
      </div>
    </Modal>
  );
};

export default SampleDetailReportCategoryModal;
